import type { TFunction } from 'i18next';
import type { DefaultOptionType } from 'antd/es/select';

export interface SiteFilterSite {
  id: string;
  name: string;
}

export const ALL_SITES_NODE_VALUE = 'group-all-sites';

/**
 * Sentinel value for the "no site assigned" group node (its children are the individual no-site
 * machines) — UI-only, stripped from the siteIds sent to the API.
 */
export const NO_SITE_NODE_VALUE = 'group-no-site';

const NO_SITE_MACHINE_PREFIX = 'no-site-machine-';

export const buildNoSiteMachineValue = (machineId: string): string => `${NO_SITE_MACHINE_PREFIX}${machineId}`;

export const isNoSiteMachineValue = (value: string): boolean => value.startsWith(NO_SITE_MACHINE_PREFIX);

/**
 * Returns undefined (no restriction) when every no-site machine is checked, or when nothing at all is
 * checked anywhere in the tree (same "select all" convention as `toApiValue`'s empty-selection case) —
 * otherwise returns the explicit (possibly empty) list of selected machine ids. Distinguishing "untouched
 * default" from "explicitly cleared while some sites remain selected" lets the caller omit `machineIds`
 * in the former case and send `machineIds: []` in the latter, instead of the two collapsing together.
 */
export const toApiMachineIds = (selectedValues: string[], noSiteMachines: SiteFilterSite[]): string[] | undefined => {
  const selectedMachineIds = selectedValues
    .filter(isNoSiteMachineValue)
    .map(value => value.slice(NO_SITE_MACHINE_PREFIX.length));
  const realSiteIds = selectedValues.filter(v => v !== NO_SITE_NODE_VALUE && !isNoSiteMachineValue(v));

  const allNoSiteMachinesSelected = selectedMachineIds.length === noSiteMachines.length;
  const nothingSelectedAtAll = realSiteIds.length === 0 && selectedMachineIds.length === 0;

  return allNoSiteMachinesSelected || nothingSelectedAtAll ? undefined : selectedMachineIds;
};

export interface SiteTreeNode extends DefaultOptionType {
  value: string;
  title: string;
  children?: SiteTreeNode[];
}

export const getSitesData = (sitesData: SiteFilterSite[]): SiteTreeNode[] =>
  sitesData.map(site => ({
    value: site.id,
    title: site.name,
  }));

export const getSiteTreeData = (
  t: TFunction,
  sitesData: SiteFilterSite[],
  noSiteMachines: SiteFilterSite[]
): SiteTreeNode[] => [
  {
    value: ALL_SITES_NODE_VALUE,
    title: t('machineDashboard.siteFilter.allSites', { count: sitesData.length }),
    children: getSitesData(sitesData),
  },
  {
    value: NO_SITE_NODE_VALUE,
    title: t('machineDashboard.siteFilter.noSitesAssigned', { count: noSiteMachines.length }),
    ...(noSiteMachines.length > 0 && {
      children: noSiteMachines.map(machine => ({
        value: buildNoSiteMachineValue(machine.id),
        title: machine.name,
      })),
    }),
  },
];

/**
 * Returns undefined (no restriction) when every real site is checked, or when nothing at all is checked
 * anywhere in the tree — otherwise returns the explicit (possibly empty) list of selected site ids.
 * Distinguishing "untouched default" from "explicitly zero real sites while only no-site machines are
 * chosen" lets the caller omit `siteIds` in the former case and send `siteIds: []` in the latter, instead
 * of the two collapsing together (which would incorrectly show every site's data).
 */
export const toApiValue = (selectedValues: string[], sitesData: SiteFilterSite[]): string[] | undefined => {
  const realSiteIds = selectedValues.filter(v => v !== NO_SITE_NODE_VALUE && !isNoSiteMachineValue(v));
  const noSiteSelected = selectedValues.some(v => v === NO_SITE_NODE_VALUE || isNoSiteMachineValue(v));

  const allSitesSelected = realSiteIds.length === getSitesData(sitesData).length;
  const nothingSelectedAtAll = realSiteIds.length === 0 && !noSiteSelected;

  return allSitesSelected || nothingSelectedAtAll ? undefined : realSiteIds;
};

export const getTriggerLabel = (selectedValues: string[], t: TFunction, sitesData: SiteFilterSite[]): string => {
  const assignedSelected = selectedValues.filter(v =>
    getSitesData(sitesData)
      .map(s => s.value)
      .includes(v)
  );
  const noSiteSelected = selectedValues.filter(v => v === NO_SITE_NODE_VALUE || isNoSiteMachineValue(v));

  // Nothing selected anywhere behaves like everything selected (same convention as toApiValue/toApiMachineIds).
  const nothingSelectedAtAll = selectedValues.length === 0;

  if (
    nothingSelectedAtAll ||
    (assignedSelected.length === getSitesData(sitesData).length && noSiteSelected.length === 0)
  ) {
    return t('machineDashboard.siteFilter.allSites', { count: sitesData.length });
  }

  if (assignedSelected.length === 0 && noSiteSelected.length > 0 && noSiteSelected.length === selectedValues.length) {
    return t('machineDashboard.siteFilter.noSitesAssigned', { count: noSiteSelected.length });
  }

  if (assignedSelected.length === 1 && noSiteSelected.length === 0 && selectedValues.length === 1) {
    const node = getSitesData(sitesData).find(s => s.value === assignedSelected[0]);
    return (node?.title as string) ?? t('machineDashboard.siteFilter.sites', { count: 1 });
  }

  return t('machineDashboard.siteFilter.sites', { count: assignedSelected.length + noSiteSelected.length });
};
